const DEFAULT_CONSOLE = "https://console.gamebank.dev";
const DEFAULT_MARKETING = "https://gamebank.dev";
const DEFAULT_DOCS = "https://docs.gamebank.dev";

/** Public contact address for sales and support. */
export const CONTACT_EMAIL = "hello@gamebank.dev";

export function contactMailto(subject = "Game Banking Engine"): string {
  return `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}`;
}

function stripTrailingSlash(url: string): string {
  return url.replace(/\/$/, "");
}

/** Public URL of the sandbox / admin console (login, /dev, /admin). */
export function consoleOrigin(): string {
  return stripTrailingSlash(import.meta.env.VITE_CONSOLE_ORIGIN ?? DEFAULT_CONSOLE);
}

/** Public URL of the marketing site (landing only). */
export function marketingOrigin(): string {
  return stripTrailingSlash(import.meta.env.VITE_MARKETING_ORIGIN ?? DEFAULT_MARKETING);
}

/** Public URL of the developer guide (static docs at / on this host). */
export function docsOrigin(): string {
  return stripTrailingSlash(import.meta.env.VITE_DOCS_ORIGIN ?? DEFAULT_DOCS);
}

function hostname(): string {
  if (typeof window === "undefined") return "";
  return window.location.hostname.toLowerCase();
}

function hostMatchesOrigin(origin: string): boolean {
  const h = hostname();
  if (!h) return false;
  const target = new URL(origin).hostname.toLowerCase();
  return h === target || h === `www.${target}`;
}

/** Apex marketing host (gamebank.dev). Local dev is treated as the console host. */
export function isMarketingHost(): boolean {
  const h = hostname();
  if (!h || h === "localhost" || h === "127.0.0.1") return false;
  return hostMatchesOrigin(marketingOrigin());
}

/** Console host (console.gamebank.dev). Local dev counts as console for full routing. */
export function isConsoleHost(): boolean {
  const h = hostname();
  if (!h || h === "localhost" || h === "127.0.0.1") return true;
  return hostMatchesOrigin(consoleOrigin());
}

/** Docs host (docs.gamebank.dev). Local dev serves docs under /docs on the console host. */
export function isDocsHost(): boolean {
  const h = hostname();
  if (!h || h === "localhost" || h === "127.0.0.1") return false;
  return hostMatchesOrigin(docsOrigin());
}

/** Auth and console paths: absolute on the marketing site, relative on the console host. */
export function consolePath(path: string): string {
  const p = path.startsWith("/") ? path : `/${path}`;
  return isConsoleHost() ? p : `${consoleOrigin()}${p}`;
}

/** Marketing pages (/, /pricing): relative on the marketing host and in local dev, absolute elsewhere. */
export function marketingPath(path: string): string {
  const p = path.startsWith("/") ? path : `/${path}`;
  const h = hostname();
  if (!h || h === "localhost" || h === "127.0.0.1" || isMarketingHost()) return p;
  return `${marketingOrigin()}${p}`;
}

/** Link back to the landing page from the console sign-in screen. */
export function marketingHome(): string {
  return isMarketingHost() ? "/" : `${marketingOrigin()}/`;
}

/** True when this path should be served only on the console host. */
export function isConsolePath(pathname: string): boolean {
  return (
    pathname === "/login"
    || pathname === "/signup"
    || pathname === "/verify-email"
    || pathname.startsWith("/admin")
    || pathname.startsWith("/dev")
  );
}

export function consoleUrl(pathname: string, search = ""): string {
  return `${consoleOrigin()}${pathname}${search}`;
}

/** Developer guide entry URL (always the docs site root in production). */
export function docsHome(): string {
  if (!hostname() || hostname() === "localhost" || hostname() === "127.0.0.1") return "/docs/index.html";
  return `${docsOrigin()}/`;
}

export function isDocsPath(pathname: string): boolean {
  return pathname === "/docs" || pathname.startsWith("/docs/");
}

export function docsUrl(pathname: string, search = ""): string {
  const origin = docsOrigin();
  if (pathname === "/docs" || pathname === "/docs/" || pathname === "/docs/index.html") return `${origin}/${search}`;
  const suffix = pathname.startsWith("/docs/") ? pathname.slice("/docs".length) : pathname;
  return `${origin}${suffix}${search}`;
}
